import { Button, Form, Toggle, useGetURLQuery, useSetQuery } from "capitalrx-components-2.0";
import cn from "classnames";
import { ChangeEvent, FC, useContext, useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation, useNavigate } from "react-router";

import AdvantageCardParentCTA from "@/components/AdvantageCardParentCTA/AdvantageCardParentCTA";
import { QueryParams } from "@/types/QueryParams";
import PharmacyResultsList from "../PharmacyResultsList";
import GenericAlternativeCard from "../PharmacyResultsList/GenericAlternativeCard";

import { FormValues, Props } from "./FindBestPriceList.props";

import { ThemeSelectorContext } from "@/context/index";
import styles from "./FindBestPriceList.module.scss";

const FindBestPriceList: FC<Props> = ({
  pharmacyResults,
  isLoading,
  genericAlternative,
  switchToGeneric,
  toggleMapView,
  mapView,
}: Props) => {
  const [query, setQuery] = useSetQuery();
  const queryParams = useGetURLQuery<QueryParams>(null);
  const { sort_by } = queryParams;
  const { client } = useContext(ThemeSelectorContext);

  const navigate = useNavigate();
  const location = useLocation();

  const noMobile = cn("NoMobile");

  const mapViewClassname = cn(
    "NoDesktop",
    { [styles.ViewToggleWhitelabel]: !!client },
  );

  const toggleClassname = cn(
    styles.ToggleComponent,
    { [styles.ToggleWhitelabel]: !!client },
  );

  const getDefaultFormValues = useMemo((): boolean => {
    if (sort_by === "distance") {
      return true;
    }
    return false;
  }, [sort_by]);

  const defaultFormValues: FormValues = {
    toggle_pharmacy_sort: getDefaultFormValues,
  };

  const handleSortByChange = (event: ChangeEvent<HTMLInputElement>) => {
    let newUrlQueryParams;
    if (event.target.checked) {
      newUrlQueryParams = setQuery({ sort_by: "distance" });
    } else {
      newUrlQueryParams = setQuery({ sort_by: "price" });
    }

    navigate({
      pathname: location.pathname,
      search: `?${newUrlQueryParams}`,
    });
  };

  const pharmacyResultsContainerClasses = cn(styles["PharmacyResultsContainer"], { NoMobile: mapView });

  const showList = !isLoading && (
    <PharmacyResultsList pharmacyResults={pharmacyResults} query={query} genericAlternative={genericAlternative} />
  );

  const loadingSkeleton = isLoading && <Skeleton className={styles.LoadingSkeleton} count={8} />;

  return (
    <div className={pharmacyResultsContainerClasses} data-testid="pharmacy-results-container">
      <div className={styles.ListContainer}>
        <GenericAlternativeCard genericAlternative={genericAlternative} switchToGeneric={switchToGeneric} />
        <div className={styles.ListControls}>
          <Form
            defaultValues={defaultFormValues}
            onSubmit={() => null}
            id="toggle-pharmacy-sort-form"
            className={styles.SortForm}
            testId="pharmacy-list-sort-form"
          >
            <span className={styles.FormLabel} data-testid="pharmacy-list-sort-label">
              Sort by:
            </span>
            <span>Price</span>
            <Toggle
              name="toggle_pharmacy_sort"
              id="toggle-pharmacy-sort"
              toggleLabel=""
              containerClassName={toggleClassname}
              onChange={handleSortByChange}
              isDisabled={isLoading}
              toggleSize="large"
            />
            <span>Distance</span>
          </Form>
          <span className={noMobile}>Estimated Amount You Pay</span>
          <Button size="xsmall" tier="secondary" className={mapViewClassname} onClick={toggleMapView} testId="mobile-toggle-map-view">
            Map View
          </Button>
        </div>
        {showList}
        {loadingSkeleton}
      </div>
      <AdvantageCardParentCTA />
    </div>
  );
};

export default FindBestPriceList;
